import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getClientBuildVersion } from '@/lib/buildVersion';
import { useReducedMotion } from '@/lib/motion';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { toast } from '@/hooks/use-toast';
import { InlineLoadingIndicator } from '@/components/ui/inline-loading';

declare global { 
  interface Window { 
    swUpdater?: {
      getBuildSha?: () => Promise<{ sha?: string; builtAt?: string }>;
      forceUpdate?: () => Promise<void>;
    }
  } 
}

interface VersionInfo {
  buildSha?: string;
  swBuildSha?: string;
  builtAt?: string;
}

/**
 * Enhanced version badge with motion controls and detailed system info
 * Shows build SHA from both app and service worker with Force Refresh capability
 */
export function VersionBadge() {
  const [version, setVersion] = useState<VersionInfo>({});  
  const [visible, setVisible] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const loadVersion = async () => {
      try {
        // Get build SHA from meta tag or import.meta
        const buildSha = document.querySelector('meta[name="build-sha"]')?.getAttribute('content') 
          || (import.meta as any).env?.NEXT_PUBLIC_BUILD_SHA 
          || 'dev';

        // Get SW build SHA if available
        let swBuildSha: string | undefined;
        let builtAt: string | undefined;
        if (window.swUpdater?.getBuildSha) {
          try {
            const swInfo = await window.swUpdater.getBuildSha();
            swBuildSha = swInfo?.sha;
            builtAt = swInfo?.builtAt;
          } catch (e) {
            // SW info not available
          }
        }

        setVersion({ buildSha, swBuildSha, builtAt });
      } catch (e) {
        // Failed to load version info
      }
    };

    loadVersion();

    // Show badge on Ctrl/Cmd + Shift + V
    const handleKeydown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'V') {
        setVisible(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, []);

  const handleForceRefresh = async () => {
    setIsUpdating(true);
    try {
      if (window.swUpdater?.forceUpdate) {
        await window.swUpdater.forceUpdate();
        setLastUpdateTime(new Date());
        toast({ 
          title: "Кэш очищен", 
          description: "Страница обновляется с новой версией..." 
        });
        // Small delay to show update state before reload
        setTimeout(() => window.location.reload(), 1000);
      } else {
        toast({ 
          title: "Принудительное обновление", 
          description: "Service Worker недоступен, перезагружаем страницу...",
          variant: "destructive"
        });
        window.location.reload();
      }
    } catch (error) {
      // Force refresh failed
      toast({ 
        title: "Ошибка обновления", 
        description: "Перезагружаем страницу обычным способом...",
        variant: "destructive" 
      });
      setIsUpdating(false);
    }
  };

  if (!visible) return null;

  const appVersion = version.buildSha?.substring(0, 7) || 'unknown';
  const swVersion = version.swBuildSha?.substring(0, 7) || 'none';
  const isSynced = version.buildSha === version.swBuildSha;
  const buildTime = version.builtAt ? new Date(version.builtAt).toLocaleString('ru-RU') : '';
  const motionStatus = reducedMotion ? 'Отключены' : 'Включены';

  return (
    <TooltipProvider>
    <div
      className="fixed right-4 z-50 animate-fade-in"
      style={{ bottom: 'calc(var(--footer-height, 56px) + var(--safe-area-bottom, 0px) + 1rem)' }}
      data-overlay-suppressable="true"
    >
      <Card className="w-80 shadow-elegant hover:shadow-glow transition-all duration-240">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <SFIcon icon={sfIcons.settings} size="sm" className="text-primary" />
              Системная информация
            </CardTitle>
            <button 
              onClick={() => setVisible(false)}
              className="text-muted-foreground hover:text-foreground transition-colors hover-scale"
            >
              ×
            </button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-3">
          {/* Version Info */}
          <div className="space-y-2 text-xs">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Приложение:</span>
              <Badge variant="outline" className="font-mono text-xs">
                {version.buildSha?.slice(0, 12) || 'unknown'}
              </Badge>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Service Worker:</span>
              <Badge variant="outline" className="font-mono text-xs">
                {version.swBuildSha?.slice(0, 12) || 'unknown'}
              </Badge>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Синхронизация:</span>
              <div className="flex items-center gap-1">
                {isSynced ? (
                  <SFIcon icon={sfIcons.checkCircle2} size="sm" className="text-success" />
                ) : (
                  <SFIcon icon={sfIcons.alertCircle} size="sm" className="text-warning" />
                )}
                <span className={`font-medium text-xs ${isSynced ? 'text-success' : 'text-warning'}`}>
                  {isSynced ? 'Синхронизировано' : 'Требуется обновление'}
                </span>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Анимации:</span>
              <Badge variant={reducedMotion ? "secondary" : "default"} className="text-xs">
                {motionStatus}
              </Badge>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Собрано:</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="font-mono text-xs text-muted-foreground cursor-help bg-transparent border-0 p-0"
                  >
                    {buildTime.split(',')[0]}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {buildTime}
                </TooltipContent>
              </Tooltip>
            </div>

            {lastUpdateTime && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Обновлено:</span>
                <span className="font-mono text-xs text-success">
                  {lastUpdateTime.toLocaleTimeString('ru-RU')}
                </span>
              </div>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button 
              onClick={handleForceRefresh}
              size="sm"
              disabled={isUpdating}
              className="flex-1 hover:shadow-md transition-shadow duration-120 text-xs"
            >
              {isUpdating ? (
                <>
                  <InlineLoadingIndicator
                    size="sm"
                    className="mr-1"
                    aria-label="Обновление"
                  />
                  Обновление...
                </>
              ) : (
                <>
                  <SFIcon icon={sfIcons.refresh} size="sm" className="mr-1" />
                  Принудительное обновление
                </>
              )}
            </Button>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Version Info and Motion Status logged for debugging
                  }}
                  className="px-3 hover-scale"
                >
                  <SFIcon icon={sfIcons.settings} size="sm" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Показать детали в консоли
              </TooltipContent>
            </Tooltip>
          </div>
          
          <div className="text-xs text-muted-foreground text-center pt-2 border-t">
            Ctrl+Shift+V для переключения
          </div>
        </CardContent>
      </Card>
    </div>
    </TooltipProvider>
  );
}

export default VersionBadge;